/**
 * TDD REFACTOR Phase - Clean Sprite Animation Types Implementation
 * Author: Claude Code
 * Version: v3.0.1
 * Modified: 2025-01-22
 * 
 * Refactored sprite animation types with improved structure and documentation.
 * All tests maintained green status during refactoring.
 */

import * as Phaser from 'phaser';

// Character type enumeration based on asset analysis
export enum CharacterType {
  GREEN = 0,   // 像素史莱姆-0-
  RED = 1,     // 像素史莱姆-1-
  PURPLE = 2,  // 像素史莱姆-2-
  BLUE = 3,    // 像素史莱姆-3-
  WHITE = 4,   // 像素史莱姆-4-
  BLACK = 5,   // 像素史莱姆-5-
  ORANGE = 6   // 像素史莱姆-6-
}

// Weapon type enumeration based on asset analysis
export enum WeaponType {
  SWORD = 'wep1',  // 像素史莱姆-wep1-
  AXE = 'wep2',    // 像素史莱姆-wep2-
  LANCE = 'wep3',  // 像素史莱姆-wep3-
  UNARMED = 'unarmed' // 徒手/无武器
}

// Animation type enumeration based on asset analysis
export enum AnimationType {
  ATTACK = 'atk',  // 8 frames (0-7)
  IDLE = 'idle',   // 11 frames (00-10)
  DIE = 'die',     // 12 frames (00-11)
  HURT = 'hurt',   // 3 frames (0-2)
  MOVE = 'mov',    // 8 frames (0-7)
  RUSH = 'rush',   // 11 frames (00-10)
  USE = 'use'      // 11 frames (00-10)
}

// Sprite game object interface (replaces SpineGameObject)
export interface SpriteGameObject {
  agentId: string;
  characterSprite: Phaser.GameObjects.Sprite;
  weaponSprite?: Phaser.GameObjects.Sprite;
  position: {
    x: number;
    y: number;
  };
  scale: {
    x: number;
    y: number;
  };
  visible: boolean;
  hasWeapon: boolean;
}

// Sprite animation state interface (replaces AnimationState)
export interface SpriteAnimationState {
  agentId: string;
  characterType: CharacterType;
  weaponType?: WeaponType;
  currentAnimation: AnimationType;
  currentFrame: number;
  totalFrames: number;
  isPlaying: boolean;
  isLooping: boolean;
  priority: number;
  timeRemaining: number;
}

// Sprite animation configuration interface
export interface SpriteAnimationConfig {
  animationType: AnimationType;
  frameCount: number;
  frameDuration: number; // milliseconds per frame
  isLooping: boolean;
  priority: number;
  filePattern: string;   // Template for file names
}

// Animation frame configuration constants
export const ANIMATION_FRAME_CONFIG = {
  [AnimationType.ATTACK]: { frameCount: 8, useDoubleDigit: false },
  [AnimationType.HURT]: { frameCount: 3, useDoubleDigit: false },
  [AnimationType.MOVE]: { frameCount: 8, useDoubleDigit: false },
  [AnimationType.IDLE]: { frameCount: 11, useDoubleDigit: true },
  [AnimationType.DIE]: { frameCount: 12, useDoubleDigit: true },
  [AnimationType.RUSH]: { frameCount: 11, useDoubleDigit: true },
  [AnimationType.USE]: { frameCount: 11, useDoubleDigit: true }
} as const;

// Animation priority constants for better maintainability
export const ANIMATION_PRIORITY = {
  DIE: 100,      // Highest priority
  HURT: 80,
  ATTACK: 70,
  RUSH: 70,
  USE: 60,
  MOVE: 50,
  IDLE: 10       // Lowest priority
} as const;

// Helper function for frame pattern logic (refactored for clarity)
export function getFramePattern(animationType: AnimationType): string {
  const config = ANIMATION_FRAME_CONFIG[animationType];
  const frameToken = config.useDoubleDigit ? '{frame:2}' : '{frame}';
  return `像素史莱姆-{characterId}-${animationType}_${frameToken}.png`;
}

// Helper function to get frame count for an animation
export function getFrameCount(animationType: AnimationType): number {
  const config = ANIMATION_FRAME_CONFIG[animationType];
  if (!config) {
    console.error(`No animation config found for: "${animationType}". Available configs:`, Object.keys(ANIMATION_FRAME_CONFIG));
    throw new Error(`Invalid animation type: ${animationType}`);
  }
  return config.frameCount;
}

// Helper function to get animation priority
export function getAnimationPriority(animationType: AnimationType): number {
  switch (animationType) {
    case AnimationType.DIE: return ANIMATION_PRIORITY.DIE;
    case AnimationType.HURT: return ANIMATION_PRIORITY.HURT;
    case AnimationType.ATTACK: return ANIMATION_PRIORITY.ATTACK;
    case AnimationType.RUSH: return ANIMATION_PRIORITY.RUSH;
    case AnimationType.USE: return ANIMATION_PRIORITY.USE;
    case AnimationType.MOVE: return ANIMATION_PRIORITY.MOVE;
    case AnimationType.IDLE: return ANIMATION_PRIORITY.IDLE;
    default: return ANIMATION_PRIORITY.IDLE;
  }
}